import type { ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import { makeRoute } from "@/components/makeRoute";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import SignInPage from "@/pages/SignInPage";
import SignUpPage from "@/pages/SignUpPage";
import ProfilePage from "@/pages/ProfilePage";
import AccountPage from "@/pages/AccountPage";
import MemberBillingPage from "@/pages/BillingPage";
import OrganizationPage from "@/pages/OrganizationPage";
import MyOrganizationsPage from "@/pages/MyOrganizationsPage";
import DirectoryPage from "@/pages/DirectoryPage";
import MemberProfilePage from "@/pages/MemberProfilePage";
import ApplyPage from "@/pages/ApplyPage";
import PayPage from "@/pages/PayPage";
import PaySuccessPage from "@/pages/PaySuccessPage";
import DiscussionsPage from "@/pages/DiscussionsPage";
import ThreadPage from "@/pages/ThreadPage";
import ResourcesPage from "@/pages/ResourcesPage";
import MyResourcesPage from "@/pages/MyResourcesPage";
import AfterSignInPage from "@/pages/AfterSignInPage";
import WelcomePage from "@/pages/WelcomePage";
import HelpPage from "@/pages/HelpPage";
import PolicyPage from "@/pages/PolicyPage";
import OrganizationsAdminPage from "@/pages/admin/OrganizationsAdminPage";
import BillingPage from "@/pages/admin/BillingPage";
import ModerationPage from "@/pages/admin/ModerationPage";
import AdminResourcesPage from "@/pages/admin/AdminResourcesPage";
import DashboardPage from "@/pages/admin/DashboardPage";
import ManageMemberPage from "@/pages/admin/ManageMemberPage";
import MembersAdminPage from "@/pages/admin/MembersAdminPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import ErrorPage from "@/pages/ErrorPage";
import NotFoundPage from "@/pages/NotFoundPage";

import { isAccountOnly } from "@/lib/redirects";
import MemberLayout from "@/layouts/MemberLayout";
import PublicLayout from "@/layouts/PublicLayout";

const route = (path: string, element: ReactNode, layout: ReactNode) =>
  makeRoute(path, element, layout, <ErrorPage />);

/** A page inside the app's shell. Readable signed out unless it says
 *  otherwise — the server decides what a preview viewer is shown. */
const memberRoute = (path: string, element: ReactNode) =>
  makeRoute(
    path,
    <ProtectedRoute>{element}</ProtectedRoute>,
    <MemberLayout />,
    <ErrorPage />
  );

/** A page with nothing to show without an account: your own profile, your
 *  billing, applying, the admin. Named one at a time on purpose.
 *
 *  Every one must be in `ACCOUNT_ONLY`, which is what signing out reads to
 *  decide where to land — staying on a page that needs an account means an
 *  immediate bounce to the sign-in form. The throw is at module load, so a
 *  route added here without the list is a failure in every test rather than a
 *  surprise for whoever signs out on it. */
const accountRoute = (path: string, element: ReactNode) => {
  if (!isAccountOnly(path)) {
    throw new Error(`${path} is an account route but is not in ACCOUNT_ONLY`);
  }
  return makeRoute(
    path,
    <ProtectedRoute requireAccount>{element}</ProtectedRoute>,
    <MemberLayout />,
    <ErrorPage />
  );
};

export const router = createBrowserRouter([
  // Public — redesigned pages carry their own chrome (see PublicLayout).
  route("/pay/success", <PaySuccessPage />, <PublicLayout />),
  // Where signing in lands: it forwards on `isMember`, which Clerk cannot
  // know when it is handed a fixed redirect URL.
  route("/after-sign-in", <AfterSignInPage />, <PublicLayout />),
  route("/sign-in/*", <SignInPage />, <PublicLayout />),
  // A door of its own since 2026-08-31. `/sign-in` still absorbs an address
  // Clerk has never seen, which is how the imported members arrive; this is
  // where the Apply button sends somebody who is plainly not signing in.
  route("/sign-up/*", <SignUpPage />, <PublicLayout />),

  // Applying is the one page with nothing to show signed out — and the one
  // member-facing page with no sidebar. It is a form somebody sits down to
  // fill in, not a place to browse from: the shell around it offered the
  // directory preview, the resources and the help centre to somebody in the
  // middle of answering questions. It keeps `requireAccount`, and stays in
  // ACCOUNT_ONLY so signing out on it lands somewhere readable.
  route(
    "/apply",
    <ProtectedRoute requireAccount>
      <ApplyPage />
    </ProtectedRoute>,
    <PublicLayout />
  ),
  route("/pay/:token", <PayPage />, <PublicLayout />),

  // The Terms of Use and the Privacy Policy, which this site serves rather
  // than linking out to Squarespace. Public with no account and no viewer at
  // all: the consent checkboxes on the registration form link here, and
  // Google's OAuth consent screen requires both to answer for anybody.
  route("/terms", <PolicyPage slug="terms-of-use" />, <PublicLayout />),
  route("/privacy", <PolicyPage slug="privacy-policy" />, <PublicLayout />),

  // Members — ported pages own their own page header, so no layout title.
  memberRoute("/directory", <DirectoryPage />),
  memberRoute("/directory/:id", <MemberProfilePage />),
  accountRoute("/profile", <ProfilePage />),
  accountRoute("/billing", <MemberBillingPage />),
  accountRoute("/organizations", <MyOrganizationsPage />),
  memberRoute("/organizations/:id", <OrganizationPage />),
  memberRoute("/discussions", <DiscussionsPage />),
  memberRoute("/discussions/:id", <ThreadPage />),
  memberRoute("/resources", <ResourcesPage />),
  accountRoute("/resources/mine", <MyResourcesPage />),
  // The front door: the welcome page in the app's own chrome. Readable signed
  // out, like everything else here — a stranger arrives at AFELL, not at a
  // sign-in form.
  memberRoute("/", <WelcomePage />),
  memberRoute("/help", <HelpPage />),
  memberRoute("/help/:slug", <HelpPage />),
  // Sign-in details: ours, on Clerk's hooks (see AccountPage).
  accountRoute("/account", <AccountPage />),

  // Admin (nav is role-gated in MemberLayout; backend enforces regardless)
  accountRoute("/admin", <DashboardPage />),
  accountRoute("/admin/organizations", <OrganizationsAdminPage />),
  accountRoute("/admin/members", <MembersAdminPage />),
  accountRoute("/admin/members/:id", <ManageMemberPage />),
  accountRoute("/admin/billing", <BillingPage />),
  accountRoute("/admin/moderation", <ModerationPage />),
  accountRoute("/admin/resources", <AdminResourcesPage />),
  accountRoute("/admin/settings", <SettingsPage />),

  // Catch-all
  route("*", <NotFoundPage />, <PublicLayout />),
]);
